import React from 'react';
import { Stack, Typography, styled, Button, CircularProgress, Box } from '@mui/material';
import { TableCustom } from 'app/components/elements/TableCustom';
import dayjs from 'dayjs';
import { useBackgroundJobsInjection } from './hook';
import { InputSelect } from '../../components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { DatePicker } from '../../components/elements/DatePicker';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import { ButtonCustom } from '../../components/elements/ButtonCustom';
const DocumentTypes = [
  { label: 'All Types', value: '' },
  { label: 'Asset Models', value: 'assetModels' },
  { label: 'Asset Types', value: 'assetTypes' },
  { label: 'Location Issues', value: 'roomIssues' },
  { label: 'Assets', value: 'submittedAssets' },
  { label: 'Assignment Sessions', value: 'assignmentSessions' }
];

export const BackgroundJobsPage = () => {
  const {
    jobs,
    pagingInfo,
    handleChangePage,
    users,
    userSelect,
    setUserSelect,
    typeFilter,
    setTypeFilter,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    handleDownload,
    downloadingJobs
  } = useBackgroundJobsInjection();

  const { auth } = useAuth();
  const isAdmin = auth?.role === Role.SUPER_ADMIN || auth?.role === Role.ADMIN;

  const headers = [
    { label: 'Requested By', width: 250 },
    { label: 'Date Requested', width: 250 },
    { label: 'Type', width: 250 },
    { label: 'Action', width: 120 }
  ];

  const dataTable = jobs.map((item) => {
    const isCompleted = item.status === 'completed';
    const isFailed = item.status === 'failed';
    const downloadProgress = downloadingJobs[item.id];
    const isDownloading = typeof downloadProgress === 'number';

    return {
      user: {
        value: (
          <Stack direction="row" alignItems="center" gap={1}>
            {item.user?.avatar ? (
              <AvatarImage src={item.user.avatar} alt="avatar" />
            ) : (
              <DefaultAvatar>{item.user?.name?.charAt(0)?.toUpperCase() || '?'}</DefaultAvatar>
            )}
            <Typography>{item.user?.name || 'Unknown User'}</Typography>
          </Stack>
        )
      },
      date: {
        value: dayjs(item.createdAt).format('MMM DD, YYYY, h:mm A')
      },
      type: {
        value: DocumentTypes.find((type) => type.value === item.type)?.label
      },
      action: {
        value: isDownloading ? (
          <Box sx={{ position: 'relative', display: 'inline-flex', ml: 2 }}>
            <CircularProgress variant="determinate" value={downloadProgress} size={32} />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <Typography
                variant="caption"
                component="div"
                color="text.secondary"
                sx={{ fontSize: '10px' }}>
                {`${Math.round(downloadProgress)}%`}
              </Typography>
            </Box>
          </Box>
        ) : isCompleted ? (
          <DownloadButton
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(item.id);
            }}>
            Download
          </DownloadButton>
        ) : isFailed ? (
          <Typography color="error" variant="body2" sx={{ ml: 1 }}>
            Failed
          </Typography>
        ) : (
          <Typography color="textSecondary" variant="body2" sx={{ ml: 1 }}>
            Pending...
          </Typography>
        )
      }
    };
  });

  return (
    <WrapperPage>
      <TypographyTitle>Background Export Jobs</TypographyTitle>

      <Stack spacing={1} mt={1} mb={1}>
        <Typography sx={{ color: 'text.secondary', fontSize: '14px' }}>
          Refresh the page to see the latest completed reports.
        </Typography>
      </Stack>

      {/* Search + Filter */}
      <Stack mb={3} gap={2}>
        <Stack flexDirection={'row'} gap={1.25} flexWrap="wrap" alignItems="flex-end">
          {isAdmin && (
            <Stack width={220} minWidth={220}>
              <InputSelect
                value={userSelect || ''}
                placeholder={'User'}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  const val = e.target.value as string;
                  setUserSelect(val === '' ? undefined : val);
                }}
                sx={{ minHeight: '56px', height: '56px', padding: 0 }}
                options={[
                  { label: 'All Users', value: '' },
                  ...(users?.map((item) => ({
                    label: item.label,
                    value: item.value
                  })) ?? [])
                ]}
              />
            </Stack>
          )}

          <Stack width={220} minWidth={220}>
            <InputSelect
              value={typeFilter || ''}
              placeholder={'Report Type'}
              onChange={(e: SelectChangeEvent<unknown>) => {
                const val = e.target.value as string;
                setTypeFilter(val === '' ? undefined : val);
              }}
              sx={{ minHeight: '56px', height: '56px', padding: 0 }}
              options={DocumentTypes}
            />
          </Stack>
          <Stack width={220} minWidth={220}>
            <DatePicker
              title={'From Date'}
              value={fromDate ? dayjs(fromDate) : null}
              onChange={(val) => setFromDate(dayjs(val).toDate())}
            />
          </Stack>

          <Stack width={220} minWidth={220}>
            <DatePicker
              title={'To Date'}
              value={toDate ? dayjs(toDate) : null}
              onChange={(val) => setToDate(dayjs(val).toDate())}
            />
          </Stack>

          <ButtonCustom
            sx={{ width: 'max-content', height: '56px' }}
            label={'Refresh'}
            onClick={() => window.location.reload()}
          />
        </Stack>
      </Stack>

      {/* Table */}
      <TableCustom
        handleChooseRow={() => {}}
        headers={headers}
        data={dataTable}
        isScrollX
        styledTableRow={{ cursor: 'default' }}
        pagingInfo={pagingInfo}
        handleChangePage={handleChangePage}
      />
    </WrapperPage>
  );
};

const WrapperPage = styled(Stack)(() => ({
  padding: '40px 30px 50px 40px',
  width: '100%',
  minHeight: '100vh'
}));

const TypographyTitle = styled(Typography)(() => ({
  fontSize: '24px',
  fontWeight: 800,
  color: '#226AAA'
}));

const AvatarImage = styled('img')({
  width: 32,
  height: 32,
  borderRadius: '50%',
  objectFit: 'cover'
});

const DefaultAvatar = styled(Stack)({
  width: 32,
  height: 32,
  borderRadius: '50%',
  backgroundColor: '#aaa',
  color: '#fff',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '14px',
  fontWeight: 600
});

const DownloadButton = styled(Button)({
  backgroundColor: '#4E8A7A', // Dark green matching mockup
  color: '#fff',
  textTransform: 'none',
  padding: '6px 16px',
  boxShadow: 'none',
  fontSize: '13px',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: '#3b6a5d',
    boxShadow: 'none'
  }
});
